import { useUserModalRootActionsStore } from '@/features/users/_shared/components/user-modal-root/user-modal-root.store';
import { TAgent } from '@/features/users/agent/types/agent.type';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CreateTeamValidationSchema, TCreateTeamValidation, useCreateTeam } from '../../../api/create-team.api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetListAgentQuery } from '@/features/users/agent/api/get-list-agent-api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { GET_LIST_TEAM_QUERY_KEY } from '../../../api/get-list-team-api';
import { queryClient } from '@/lib/react-query';
import { handleDomBubbleEvent } from '@/utils/dom';
import Input from '@/components/ui/input';
import GenericMultiselectDropdown from '@/components/ui/multiselect-dropdown/generic-multiselect-dropdown';
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalContent,
  ModalFooter,
  ModalConfirmButton,
  ModalCloseButton,
} from '@/components/_ui/modal';
import { Spinner } from '@/components/_ui/spinner';
import type { AxiosErrorCustom } from '@/lib/axios';

export const TOAST_MESSAGES_CREATE_TEAM = {
  CREATE_TEAM_ERROR: 'Create team failed!',
  CREATE_TEAM_SUCCESS: 'Create team successfully!',
};

export type TCreateTeamModalType = 'CreateTeamModal';
export type ICreateTeamModalProps = Record<string, never>;

const CreateTeamModal = memo((_props: ICreateTeamModalProps) => {
  const userModalRootActions = useUserModalRootActionsStore();
  const showToast = useCustomToast();
  const [selectedAgents, setSelectedAgents] = useState<TAgent[]>([]);

  const isOpenCreateTeamModal = useMemo(() => {
    return userModalRootActions.checkIsOpenModal('CreateTeamModal');
  }, [userModalRootActions]);

  const form = useForm<TCreateTeamValidation>({
    mode: 'onChange',
    resolver: zodResolver(CreateTeamValidationSchema),
  });

  /**
   * Get Agents
   */
  const { data: agentData, isLoading: isLoadingGetTeam } = useGetListAgentQuery({
    params: {
      search: '',
      page: 1,
      limit: 500,
      sortDir: 'DESC',
      sortBy: 'createdAt',
    },
    select: (data) => data.payload?.rows || [],
  });

  const mutationCreateTeam = useCreateTeam({
    mutationConfig: {
      onError: (error: AxiosErrorCustom) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_CREATE_TEAM.CREATE_TEAM_ERROR;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: [GET_LIST_TEAM_QUERY_KEY] });
        form.reset();
        setSelectedAgents([]);
        userModalRootActions.closeModal('CreateTeamModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_CREATE_TEAM.CREATE_TEAM_SUCCESS });
      },
    },
  });

  const handleChangeSelectedAgents = useCallback(
    (value: TAgent[]) => {
      setSelectedAgents(value);
      form.setValue(
        'userIds',
        value.map((agent) => agent.userId)
      );
    },
    [form]
  );

  const onSubmit = useCallback(
    (data: TCreateTeamValidation) => {
      mutationCreateTeam.mutate({
        name: data.name,
        userIds: data?.userIds || [],
      });
    },
    [mutationCreateTeam]
  );

  const handleClose = useCallback(() => {
    form.reset();
    setSelectedAgents([]);
    userModalRootActions.closeModal('CreateTeamModal');
  }, [form, userModalRootActions]);

  return (
    <Modal isOpen={isOpenCreateTeamModal} lockBodyScroll onClose={handleClose} className="p-[32px] w-[400px]">
      <ModalHeader>
        <ModalTitle>Create Team</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <form
          onSubmit={(e) => {
            handleDomBubbleEvent(e);
            form.handleSubmit(onSubmit)(e);
          }}
          id="add-team-form"
          className="grid gap-[16px]"
        >
          <div className="grid gap-[4px]">
            <label htmlFor="name" className="text-[#4B4B4B] text-[12px] font-normal">
              Name*
            </label>
            <Input id="name" placeholder="Enter Name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.name.message}</p>
            )}
          </div>

          <div className="grid gap-[4px]">
            <label htmlFor="team" className="text-[#4B4B4B] text-[12px] font-normal">
              Agents (Optional)
            </label>
            <GenericMultiselectDropdown
              isLoading={isLoadingGetTeam}
              options={agentData || []}
              value={selectedAgents}
              onChange={handleChangeSelectedAgents}
              placeholder="Select Agents"
              searchPlaceholder="Search agents..."
              labelKey="fullname"
              valueKey="userId"
            />
            {form.formState.errors.userIds && (
              <p className="text-red-500 text-[12px] font-normal">{form.formState.errors.userIds.message}</p>
            )}
          </div>
        </form>
      </ModalContent>
      <ModalFooter>
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleClose();
          }}
        >
          Cancel
        </ModalCloseButton>
        <ModalConfirmButton form="add-team-form" type="submit" disabled={mutationCreateTeam.isPending}>
          {mutationCreateTeam.isPending ? <Spinner isLoading={true} className="w-[18px] h-[18px]" /> : null} Create
        </ModalConfirmButton>
      </ModalFooter>
    </Modal>
  );
});

CreateTeamModal.displayName = 'CreateTeamModal';
export default CreateTeamModal;
